import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStreamAuth } from '../hooks/useStreamAuth';
import { useWatchlist } from '../hooks/useWatchlist';
import { useContinueWatching } from '../hooks/useContinueWatching';
import { useMyReactions } from '../hooks/useMyReactions';
import { useMyPurchases } from '../hooks/useMyPurchases';
import { useAppLoad } from '../contexts/AppLoadContext';
import { userDataService } from '../services/UserDataService';
import ChannelCard from '../components/ChannelCard';
import LoadingSpinner from '../components/auth/LoadingSpinner';
import Header from '../components/common/Header';
import ProgressBar from '../components/common/ProgressBar';

const MyListsPage = () => {
  const { loginWithRedirect } = useAuth0();
  const { user, isAuthenticated } = useStreamAuth();
  const navigate = useNavigate();
  const { tab } = useParams();
  
  const {
    watchlistData: watchlist,
    loading: watchlistLoading,
    error: watchlistError,
    isRemoving,
    removeFromWatchlist,
  } = useWatchlist();
  const { continueWatchingData: continueWatching, loading: continueWatchingLoading } =
    useContinueWatching();
  const { 
    reactionsData: myReactions, 
    loading: reactionsLoading, 
    error: reactionsError,
    removeReaction 
  } = useMyReactions();
  const {
    purchasesData: myPurchases,
    subscriptionData: mySubscription,
    loading: purchasesLoading,
    error: purchasesError
  } = useMyPurchases();
  const { categories } = useAppLoad();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define available tabs with their configurations
  const tabs = [
    {
      id: 'watchlist',
      label: 'My Favorites',
      icon: '❤️',
      count: watchlist.length,
      path: '/my-lists/watchlist'
    },
    {
      id: 'continue-watching',
      label: 'Continue Watching',
      icon: '▶️',
      count: continueWatching.length,
      path: '/my-lists/continue-watching'
    },
    {
      id: 'reactions',
      label: 'My Reactions',
      icon: '👍',
      count: myReactions.length,
      path: '/my-lists/reactions'
    },
    {
      id: 'purchases',
      label: 'My Purchases',
      icon: '🛒',
      count: myPurchases.length,
      path: '/my-lists/purchases'
    }
  ];

  // Determine active tab from URL parameter, default to 'watchlist'
  const activeTab = tab && tabs.find(t => t.id === tab) ? tab : 'watchlist';

  // Redirect to default tab if no tab specified
  useEffect(() => {
    if (!tab) {
      navigate('/my-lists/watchlist', { replace: true });
    }
  }, [tab, navigate]);

  useEffect(() => {
    if (isAuthenticated && user) {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  // Combine loading states
  const isLoadingData = continueWatchingLoading || reactionsLoading || purchasesLoading || isLoading || (watchlistLoading && watchlist.length === 0);
  const hasError = watchlistError || reactionsError || purchasesError || error;

  const handleRemoveFromWatchlist = async channelId => {
    try {
      await removeFromWatchlist(channelId);
    } catch (error) {
      console.error('MyListsPage: Error removing from watchlist:', error);
      setError(error.message);
    }
  };

  const handleRemoveReaction = async channelId => {
    console.log('🗑️ MyListsPage: handleRemoveReaction called with channelId:', channelId);
    try {
      const success = await removeReaction(channelId);
      console.log('🗑️ MyListsPage: removeReaction returned:', success);
      if (!success) {
        console.warn('🗑️ MyListsPage: Reaction removal may have failed on server');
      }
    } catch (error) {
      console.error('MyListsPage: Error removing reaction:', error);
      setError(error.message);
    }
  };

  const handleItemClick = async item => {
    try {
      const slug = item.parent_channel?.slug || item.slug;

      if (item.content_type === 'series' || item.childChannels?.length > 0) {
        const latestWatchedEpisode = userDataService.findLatestWatchedEpisode(item._id);
        if (latestWatchedEpisode) {
          navigate(`/watch/${slug}?${latestWatchedEpisode.episode._id}`);
        } else {
          navigate(`/watch/${slug}`);
        }
      } else {
        navigate(`/watch/${slug}`);
      }
    } catch (error) {
      console.error('MyListsPage: Error handling item click:', error);
      navigate(`/watch/${item.slug}`);
    }
  };

  const handleTabClick = (tabId) => {
    const selectedTab = tabs.find(t => t.id === tabId);
    if (selectedTab) {
      navigate(selectedTab.path);
    }
  };

  const renderWatchlistContent = () => {
    if (watchlist.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">❤️</div>
          <h3 className="text-xl font-semibold text-white mb-2">Your list is empty</h3>
          <p className="text-white/70 mb-6">
            Add movies and shows to your list by clicking the heart icon
          </p>
          <button
            className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/80 transition-colors"
            onClick={() => navigate('/')}
          >
            Browse Content
          </button>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {watchlist.map((item, index) => (
          <div key={item._id || index} className="relative">
            <ChannelCard
              channel={item}
              style="watchlist"
              index={index}
              externalClickHandle={() => handleItemClick(item)}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleRemoveFromWatchlist(item._id);
              }}
              disabled={isRemoving(item._id)}
              className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-700 transition-colors"
              title="Remove from list"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    );
  };

  const renderContinueWatchingContent = () => {
    if (continueWatching.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">▶️</div>
          <h3 className="text-xl font-semibold text-white mb-2">Nothing to continue</h3>
          <p className="text-white/70 mb-6">
            Start watching something to see your progress here
          </p>
          <button
            className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/80 transition-colors"
            onClick={() => navigate('/')}
          >
            Start Watching
          </button>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {continueWatching.map((item, index) => (
          <div key={item._id || index} className="relative">
            <ChannelCard
              channel={item}
              style="continue-watching"
              index={index}
              externalClickHandle={() => handleItemClick(item)}
            />
            {item.progress && (
              <div className="absolute bottom-2 left-2 right-2">
                <ProgressBar 
                  progress={item.progress} 
                  className="h-1 bg-white/20"
                  fillClassName="bg-accent"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderReactionsContent = () => {
    console.log('🔍 MyListsPage: Rendering reactions content:', myReactions);
    
    if (myReactions.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">👍</div>
          <h3 className="text-xl font-semibold text-white mb-2">No reactions yet</h3>
          <p className="text-white/70 mb-6">
            Like or dislike content to see your reactions here
          </p>
          <button
            className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/80 transition-colors"
            onClick={() => navigate('/')}
          >
            Explore Content
          </button>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {myReactions.map((item, index) => {
          console.log('🔍 MyListsPage: Rendering reaction item:', {
            _id: item._id,
            user_reaction: item.user_reaction,
            reaction: item.reaction,
            title: item.title
          });
          
          return (
            <div key={item._id || index} className="relative">
              <ChannelCard
                channel={item}
                style="reactions"
                index={index}
                externalClickHandle={() => handleItemClick(item)}
              />
              <div className="absolute top-2 left-2 bg-black/70 rounded-full w-8 h-8 flex items-center justify-center">
                <span className="text-lg">
                  {item.user_reaction === 'like' ? '👍' : 
                   item.user_reaction === 'dislike' ? '👎' : 
                   item.user_reaction === 'double_like' ? '❤️' : 
                   item.user_reaction === 'double_dislike' ? '💔' : '?'}
                </span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleRemoveReaction(item.dspro_id || item._id);
                }}
                className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-700 transition-colors"
                title="Remove reaction"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const renderPurchasesContent = () => {
    console.log('🛒 MyListsPage: Rendering purchases content:', myPurchases);
    
    const hasPurchases = myPurchases.length > 0;
    const hasSubscription = !!mySubscription;
    const hasContent = hasPurchases || hasSubscription;
    
    // Format date helper
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    };

    // Get expiration text
    const getExpirationText = (purchase) => {
      if (purchase.never_expires) {
        return 'Lifetime';
      }
      if (purchase.rental_end) {
        return `Expires ${formatDate(purchase.rental_end)}`;
      }
      if (purchase.product?.duration) {
        const { number, unit } = purchase.product.duration;
        return `${number} ${unit}${number > 1 ? 's' : ''}`;
      }
      return 'N/A';
    };

    // Handle watch button click
    const handleWatch = (purchase) => {
      const channel = purchase.channels?.[0];
      if (channel?.slug) {
        navigate(`/watch/${channel.slug}`);
      }
    };

    // Handle view details (open PDP)
    const handleViewDetails = (purchase) => {
      const channel = purchase.channels?.[0];
      if (channel?.slug) {
        navigate(`/?channel=${channel.slug}`);
      }
    };

    // Empty state
    if (!hasContent) {
      return (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🛒</div>
          <h3 className="text-xl font-semibold text-white mb-2">No purchases yet</h3>
          <p className="text-white/70 mb-6">
            Purchase content to see your library here
          </p>
          <button
            className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/80 transition-colors"
            onClick={() => navigate('/')}
          >
            Browse Content
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Active Subscription Section */}
        {hasSubscription && (
          <div className="mb-2">
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Active Subscription
            </h3>
            <div className="bg-gradient-to-r from-green-500/10 to-transparent border border-green-500/30 rounded-lg p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-base font-semibold text-white">{mySubscription.product?.name || 'Subscription'}</h4>
                  <p className="text-white/70 text-sm mt-1">{mySubscription.product?.description || 'Full access to content'}</p>
                </div>
                <span className="px-3 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full text-sm font-medium">
                  Active
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Purchases List */}
        {hasPurchases && (
          <div>
            {hasSubscription && (
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                Purchases ({myPurchases.length})
              </h3>
            )}

            <div className="space-y-4">
              {myPurchases.map((purchase, index) => {
                const channel = purchase.channels?.[0];
                const posterUrl = channel?.poster || channel?.spotlight_poster || null;
                const productName = purchase.product?.name || channel?.title || 'Unknown Product';
                const price = purchase.product?.price || 'N/A';
                const productType = purchase.product?.product_type?.toUpperCase() || 'PURCHASE';
                const adsEnabled = purchase.product?.ads_enabled;

                return (
                  <div
                    key={purchase._id || index}
                    className="bg-gray-800/30 rounded-lg overflow-hidden hover:bg-gray-800/50 transition-colors"
                  >
                    <div className="flex items-stretch">
                      {/* Poster Image */}
                      <div className="w-24 sm:w-32 md:w-40 flex-shrink-0 bg-gray-900">
                        {posterUrl ? (
                          <img 
                            src={posterUrl} 
                            alt={productName}
                            className="w-full h-full object-cover"
                            style={{ aspectRatio: '2/3' }}
                          />
                        ) : (
                          <div 
                            className="w-full h-full flex items-center justify-center bg-gray-700 text-gray-500"
                            style={{ aspectRatio: '2/3' }}
                          >
                            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Transaction Details */}
                      <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between">
                        <div>
                          {/* Title Row */}
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <h4 className="text-lg font-semibold text-white line-clamp-1">
                              {productName}
                            </h4>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {/* Product Type Badge */}
                              <span className="px-2 py-0.5 bg-accent/20 text-accent border border-accent/30 rounded text-xs font-medium">
                                {productType}
                              </span>
                              {/* Ad-Free Badge */}
                              {!adsEnabled && (
                                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 border border-green-500/30 rounded text-xs font-medium">
                                  Ad-Free
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Info Grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm mb-3">
                            <div>
                              <span className="text-white/50">Purchased</span>
                              <p className="text-white">{formatDate(purchase.rental_start)}</p>
                            </div>
                            <div>
                              <span className="text-white/50">Price</span>
                              <p className="text-white font-medium">{price}</p>
                            </div>
                            <div>
                              <span className="text-white/50">Access</span>
                              <p className="text-white">{getExpirationText(purchase)}</p>
                            </div>
                          </div>

                          {/* Platform Badge */}
                          <div className="text-xs text-white/40">
                            Purchased via {purchase.platform || 'web'}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        {channel?.slug && (
                          <div className="flex gap-3 mt-4">
                            <button
                              onClick={() => handleWatch(purchase)}
                              className="bg-accent hover:bg-accent/80 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors inline-flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                              Watch Now
                            </button>
                            <button
                              onClick={() => handleViewDetails(purchase)}
                              className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                            >
                              View Details
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'watchlist':
        return renderWatchlistContent();
      case 'continue-watching':
        return renderContinueWatchingContent();
      case 'reactions':
        return renderReactionsContent();
      case 'purchases':
        return renderPurchasesContent();
      default:
        return renderWatchlistContent();
    }
  };

  const getPageTitle = () => {
    const currentTab = tabs.find(t => t.id === activeTab);
    return currentTab ? currentTab.label : 'My Stuff';
  };

  // Authentication check
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-primary text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Sign in required</h2>
          <p className="text-white/70 mb-6">You need to sign in to view your lists</p>
          <button
            onClick={() => loginWithRedirect()}
            className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/80 transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-primary">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex gap-8">
            {/* Sidebar skeleton */}
            <div className="w-64 bg-gray-900 rounded-lg p-6">
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-12 bg-gray-800 rounded animate-pulse"></div>
                ))}
              </div>
            </div>
            {/* Content skeleton */}
            <div className="flex-1">
              <div className="flex justify-center items-center h-64">
                <LoadingSpinner />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div className="min-h-screen bg-primary">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex gap-8">
            <div className="w-64 bg-gray-900 rounded-lg"></div>
            <div className="flex-1">
              <div className="text-center py-12">
                <div className="text-6xl mb-4">⚠️</div>
                <h3 className="text-xl font-semibold text-white mb-2">Something went wrong</h3>
                <p className="text-white/70 mb-6">{hasError}</p>
                <button
                  className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/80 transition-colors"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary text-white">
      <Header />
      
      {/* Use same container as homepage */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Vertical Sidebar Navigation */}
          <div className="w-64 bg-gray-900 rounded-lg">
            <div className="p-6">
              <h1 className="text-2xl font-bold text-white mb-8">My Stuff</h1>
              
              <nav className="space-y-2">
                {tabs.map((tabConfig) => (
                  <button
                    key={tabConfig.id}
                    onClick={() => handleTabClick(tabConfig.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                      activeTab === tabConfig.id
                        ? 'bg-accent text-white'
                        : 'text-white/70 hover:text-white hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">{tabConfig.icon}</span>
                      <span className="font-medium">{tabConfig.label}</span>
                    </div>
                    <span className={`text-sm px-2 py-1 rounded-full ${
                      activeTab === tabConfig.id
                        ? 'bg-white/20'
                        : 'bg-gray-700'
                    }`}>
                      {tabConfig.count}
                    </span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            {/* Page Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">{getPageTitle()}</h2>
              <p className="text-white/70">
                {activeTab === 'watchlist' && 'Your favorite movies and shows'}
                {activeTab === 'continue-watching' && 'Pick up where you left off'}
                {activeTab === 'reactions' && 'Content you\'ve liked or disliked'}
                {activeTab === 'purchases' && 'Your purchased content library'}
              </p>
            </div>

            {/* Content */}
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyListsPage;